/**
 * Presentational arena pieces for Distillation Defender.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import type { DefenderGenerator } from "../../algorithms/distillationDefender.js";
import { panelTokens } from "@csk/demo-ui";
import {
  ArcadePlayfieldMarker,
  arcadeLaneCenter,
} from "../../../../games/index.js";
export function Lane({
  lane,
  index,
  count,
  active,
  onSelect,
}: {
  readonly lane: string;
  readonly index: number;
  readonly count: number;
  readonly active: boolean;
  readonly onSelect: (lane: number) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(index);
      }}
      className="absolute bottom-0 top-0 border-0 border-l border-solid bg-transparent text-[11px] font-bold"
      style={{
        left: arcadeLaneCenter(index, count),
        width: `${100 / count}%`,
        transform: "translateX(-50%)",
        borderColor: active
          ? "rgba(125,211,252,0.75)"
          : "rgba(148,163,184,0.2)",
        color: active ? "#cffafe" : panelTokens.muted,
      }}
      aria-label={`Align projector with ${lane}`}
    >
      <span className="absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-black/40 px-2 py-1">
        {lane}
      </span>
    </button>
  );
}
export function GeneratorTile({
  generator,
  laneCount,
  legal,
}: {
  readonly generator: DefenderGenerator;
  readonly laneCount: number;
  readonly legal: boolean;
}): React.JSX.Element {
  const color = colorForGenerator(generator);
  return (
    <ArcadePlayfieldMarker
      x={arcadeLaneCenter(generator.lane, laneCount)}
      y={generator.y}
      className="flex h-[48px] w-[58px] flex-col items-center justify-center rounded-md border border-solid px-1 text-center shadow-lg sm:w-[76px]"
      borderColor={legal ? "#fff7ed" : color}
      background={
        generator.kind === "violator"
          ? "rgba(244,63,94,0.22)"
          : "rgba(52,211,153,0.18)"
      }
      color={generator.kind === "violator" ? "#ffe4e6" : "#dcfce7"}
      boxShadow={
        legal
          ? "0 0 30px rgba(251,113,133,0.55)"
          : "0 10px 28px rgba(0,0,0,0.24)"
      }
    >
      <span className="font-mono text-[10px] font-black">
        {generator.axiom}
      </span>
      <span className="max-w-full truncate text-[10px] font-bold">
        {generator.label}
      </span>
    </ArcadePlayfieldMarker>
  );
}
export function defenderFlashColor(
  tone: "hit" | "miss" | "win" | "escape",
): string {
  if (tone === "hit") return "rgba(125,211,252,0.28)";
  if (tone === "miss") return "rgba(251,113,133,0.22)";
  if (tone === "win") return "rgba(52,211,153,0.24)";
  return "rgba(251,113,133,0.28)";
}
function colorForGenerator(generator: DefenderGenerator): string {
  if (generator.kind === "violator") return panelTokens.violation;
  if (generator.axiom === "A2") return panelTokens.a2;
  if (generator.axiom === "A3") return panelTokens.a3;
  if (generator.axiom === "A4") return panelTokens.a4;
  return panelTokens.immune;
}
